import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/TopNav";
import { ShariahScreenCard } from "@/components/halal/ShariahScreenCard";
import { Delta, HalalBadge, LiveBadge, EmptyState } from "@/components/common";
import { LineTrend } from "@/components/Charts";
import { TradeSheet } from "@/components/order/TradeSheet";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtCcy, fmtCompact, timeAgo } from "@/lib/format";
import { toastError } from "@/lib/toastError";
import { useLiveQuotes } from "@/hooks/useLiveQuotes";
import { ArrowLeft, Newspaper } from "lucide-react";

export default function StockDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [candles, setCandles] = useState([]);
  const [fund, setFund] = useState(null);
  const [news, setNews] = useState([]);
  const [range, setRange] = useState(90);
  const [ticket, setTicket] = useState(false);

  const source = symbol?.includes(".") ? "gcc" : "global";
  const { quotes: liveQuotes, connected } = useLiveQuotes(symbol ? [symbol] : []);
  const tick = liveQuotes[symbol];

  const load = useCallback(async () => {
    try {
      const [q, c, f, n] = await Promise.all([
        api.get(`/market/quote/${symbol}`), api.get(`/market/candles/${symbol}?days=${range}`),
        api.get(`/market/fundamentals/${symbol}`), api.get(`/market/news/${symbol}`),
      ]);
      setQuote(q.data); setCandles(c.data); setFund(f.data); setNews(n.data);
    } catch (e) { toastError("Couldn't load this instrument", e, { id: "stock-detail-load" }); }
  }, [symbol, range]);
  useEffect(() => { load(); const iv = setInterval(() => api.get(`/market/quote/${symbol}`).then(r => setQuote(r.data)).catch((e) => console.error("Quote poll failed:", e)), connected ? 30000 : 4000); return () => clearInterval(iv); }, [symbol, range, load, connected]);

  if (!quote) return <AppShell><EmptyState title="Loading..." /></AppShell>;
  const price = tick?.price ?? quote.price;
  const changePct = tick?.change_pct ?? quote.change_pct;
  const feed = tick ? (tick.source === "alpaca_iex" ? "Alpaca IEX" : "Tharwy feed") : null;

  return (
    <AppShell>
      <Button variant="ghost" size="sm" className="mb-3 gap-1.5" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" />Back</Button>

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4 sm:space-y-5">
          <Card className="p-5 shadow-premium card-premium" data-testid="stock-detail-header">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2"><h1 className="text-2xl font-semibold">{quote.symbol}</h1>{quote.halal && <HalalBadge />}<LiveBadge />{feed && <span className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--tharwy-indigo)]" data-testid="stock-detail-feed">{feed}</span>}</div>
                <div className="text-muted-foreground">{quote.name} · {quote.market} · {quote.sector}</div>
              </div>
              <div className="text-end">
                <div className="text-3xl font-semibold numeric" data-testid="stock-detail-price">{fmtCcy(price, quote.currency)}</div>
                <Delta value={changePct} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button className="flex-1" onClick={() => setTicket(true)} data-testid="stock-detail-buy-button">Buy / Sell</Button>
            </div>
          </Card>

          <Card className="p-5 shadow-premium">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Price Chart</div>
              <Tabs value={String(range)} onValueChange={(v) => setRange(Number(v))}>
                <TabsList>{[[30,"1M"],[90,"3M"],[180,"6M"],[365,"1Y"]].map(([d,l]) => <TabsTrigger key={d} value={String(d)}>{l}</TabsTrigger>)}</TabsList>
              </Tabs>
            </div>
            <div data-testid="stock-detail-chart">
              <LineTrend data={candles} dataKey="close" color={quote.change_pct >= 0 ? "#5C7020" : "#C2413A"} height={320} />
            </div>
          </Card>

          <Card className="p-5 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Newspaper className="h-4 w-4" />News</div>
            <div className="divide-y">
              {news.map((n, i) => (
                <div key={`${n.time}-${n.headline}`} className="py-3">
                  <div className="text-sm font-medium">{n.headline}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{n.category} · {n.source} · {timeAgo(n.time)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4 sm:space-y-5">
        <Card className="p-5 shadow-premium h-fit">
          <div className="text-sm font-medium mb-3">Fundamentals</div>
          {fund && (
            <div className="space-y-2 text-sm">
              {[["Market cap", fmtCompact(fund.market_cap)], ["P/E ratio", fund.pe_ratio], ["EPS", fund.eps],
                ["Dividend yield", `${fund.dividend_yield}%`], ["Beta", fund.beta], ["Profit margin", `${fund.profit_margin}%`],
                ["52W high", fmtCcy(quote.high_52w, quote.currency)], ["52W low", fmtCcy(quote.low_52w, quote.currency)],
                ["Volume", Number(quote.volume).toLocaleString()]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="numeric font-medium">{v}</span></div>
              ))}
            </div>
          )}
        </Card>
        <ShariahScreenCard symbol={symbol} />
        </div>
      </div>

      <TradeSheet open={ticket} onOpenChange={setTicket} symbol={symbol} source={source} onDone={load} />
    </AppShell>
  );
}
